import { Feature } from 'vue-media-annotator/src/track';

import { defaultAnnotationClass, mod } from '@/utils';
import { Annotation, RectBounds } from '@/types';
import store from '@/store';

import {
  trackMap,
  sortedTracks,
  selectedTrackId,
  addTrack,
  selectTrack,
  removeTrack,
} from '../state';

import {
  deleteTrackFeature,
  transferTrackFeatureOwnership,
  annotationIndexToTrackId,
  trackIdToAnnotationIndex,
} from '../utils';

// Dataset module is most used, so assign aliases
const {
  state: { dataset: datasetState },
  getters: { dataset: datasetGetters },
  commit: { dataset: datasetCommit },
  // dispatch: { dataset: datasetDispatch },
} = store;

// Change the currently selected annotation's class
export function setAnnotationClass(className: string) {
  const { currentAnnotation } = datasetGetters;
  const { currentFrameIndex } = datasetState;
  if (currentAnnotation === null || selectedTrackId.value === null || currentFrameIndex === null) {
    return;
  }

  // Update annotation
  datasetCommit.updateCurrentAnnotation({ ...currentAnnotation, class: className });

  // Update track
  const track = trackMap.get(selectedTrackId.value);
  if (track) {
    transferTrackFeatureOwnership(track.trackId, className);
  }

  // Bounds changed, frame is no longer predicted
  store.dispatch.dataset.setFramePredicted({ index: currentFrameIndex, predicted: false });
}

// Change the currently selected annotation's class to the next/previous option
export function rotateAnnotationClass(delta: 1 | -1) {
  const { currentFrameIndex } = datasetState;
  const { currentAnnotation } = datasetGetters;
  const { annotationClasses } = store.getters.application;

  if (
    annotationClasses === null
    || currentAnnotation === null
    || currentFrameIndex === null
  ) { return; }

  // Find current class index
  const currentAnnotationClassIndex = annotationClasses.findIndex(
    (aClass) => (currentAnnotation && currentAnnotation.class === aClass),
  );

  // Get next class in array, default to first class
  const nextClass = currentAnnotationClassIndex === -1
    ? annotationClasses[0]
    : annotationClasses[mod((currentAnnotationClassIndex + delta), annotationClasses.length)];

  setAnnotationClass(nextClass);
}

// The payload value represents either a trackId, or an annotation index.
export function setSelectedAnnotation(value: number | null, valueIsTrackId: boolean) {
  // Set both to null
  if (value === null) {
    datasetCommit.setCurrentAnnotationIndex(null);
    selectTrack(null);

    return;
  }

  // Return if maps can't be accessed
  if (trackIdToAnnotationIndex.value === null || annotationIndexToTrackId.value === null) {
    return;
  }

  let annotationIndex: number | null;
  let trackId: number | null;
  if (valueIsTrackId) {
    trackId = value;
    annotationIndex = trackIdToAnnotationIndex.value[value];
  } else {
    annotationIndex = value;
    trackId = annotationIndexToTrackId.value[value];
  }

  // Set current annotation index and select track
  datasetCommit.setCurrentAnnotationIndex(annotationIndex);
  selectTrack(trackId, true);
}

// Either start or stop drawing an annotation
export function setDrawingAnnotation(drawing = true) {
  const { currentFrameIndex } = datasetState;

  if (currentFrameIndex === null) { return; }

  datasetCommit.setDrawingAnnotation(drawing);
  if (drawing) {
    // New Track, add and select
    selectTrack(addTrack(currentFrameIndex, defaultAnnotationClass.class).trackId, true);
    return;
  }

  // Not drawing
  if (selectedTrackId.value !== null) {
    const track = trackMap.get(selectedTrackId.value);
    if (track !== undefined && track.featureIndex.length === 0) {
      // Deselect track before removing, to avoid errors
      selectTrack(null);

      // Remove track since it was created but not drawn
      removeTrack(track.trackId);
    }
  }
}

// This is called when a track has been either added or updated
export function updateAnnotationBounds(bounds: RectBounds) {
  const { currentFrameIndex, drawingAnnotation } = datasetState;
  const { currentAnnotation } = datasetGetters;

  if (selectedTrackId.value === null || currentFrameIndex === null) {
    return;
  }

  // The track that was just added or updated is always selected
  const track = trackMap.get(selectedTrackId.value);
  if (track) {
    track.setFeature({ frame: currentFrameIndex, bounds, keyframe: true, interpolate: false });

    if (drawingAnnotation || currentAnnotation === null) {
      const [type] = track.getType()!;
      const annotation: Annotation = { type: 'box', class: type, coordinates: bounds };
      datasetCommit.addAnnotation({
        frame: currentFrameIndex,
        annotation,
      });
    } else {
      const newAnnotation: Annotation = { ...currentAnnotation, coordinates: bounds };
      datasetCommit.updateCurrentAnnotation(newAnnotation);
    }
  }

  // If we were just drawing, set drawing to false
  if (drawingAnnotation) {
    setDrawingAnnotation(false);
  }

  // Bounds changed, frame is no longer predicted
  store.dispatch.dataset.setFramePredicted({ index: currentFrameIndex, predicted: false });
}

// Called when this annotation doesn't exist in the track store or the state
export function addAnnotation(frame: number, annotation: Annotation) {
  const feature: Feature = {
    frame,
    bounds: annotation.coordinates,
    interpolate: false,
    keyframe: true,
    attributes: { confidence: annotation.model_score },
  };

  // Get tracks that are free on this frame, and match the class
  const possibleTracks = sortedTracks.value.filter(
    (track) => (
      track.getType()?.[0] === annotation.class
      && track.getFeature(frame)[0] === null
    ),
  );

  // If no possible track candidates, create a new track
  const track = possibleTracks.length
    ? possibleTracks[0]
    : addTrack(frame, annotation.class);

  // Assign the feature to the track
  track.setFeature(feature);

  // Set annotation in store
  datasetCommit.addAnnotation({ frame, annotation });
}

// Clear all annotations for the specified frame
export function clearAnnotations(frameIndex: number) {
  const { dataset } = datasetState;

  const frame = dataset?.[frameIndex];
  const annotations = dataset?.[frameIndex].annotations.regions;
  if (frame === undefined || annotations === undefined) {
    return;
  }

  // De-select annotations
  datasetCommit.setCurrentAnnotationIndex(null);

  // Remove annotations
  frame.annotations.regions = [];
  datasetCommit.updateFrame({ index: frameIndex, frame });

  // Find all tracks with features on this frame
  const possibleTracks = sortedTracks.value.filter(
    (track) => track.getFeature(frameIndex)[0] !== null,
  );

  // Remove these features
  possibleTracks.forEach((track) => {
    deleteTrackFeature(track, frameIndex);
  });

  // Annotations deleted, frame is no longer predicted
  store.dispatch.dataset.setFramePredicted({ index: frameIndex, predicted: false });
}

// Remove the currently selected annotation
export function removeCurrentAnnotation() {
  const { currentFrameIndex, currentAnnotationIndex } = datasetState;

  datasetCommit.removeAnnotation(currentAnnotationIndex);
  datasetCommit.setCurrentAnnotationIndex(null);

  // Can't operate
  if (selectedTrackId.value === null || currentFrameIndex === null) {
    return;
  }

  const track = trackMap.get(selectedTrackId.value);
  if (track === undefined) {
    return;
  }

  deleteTrackFeature(track, currentFrameIndex);

  // Annotation deleted, frame is no longer predicted
  store.dispatch.dataset.setFramePredicted({ index: currentFrameIndex, predicted: false });
}
